import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Spinner, Typography } from "@/components/atoms";
import { useGetVehicleCategoriesQuery } from "@/redux/apis/config/pricing/vehicleCategory/vehicleCategoryApi";
import { AddTransferTypeFormData } from "../form";
import { ExcludedCategoriesTable } from "./ExcludedCategoriesTable";

export const TransferTypeExcludedCategories = () => {
  const { setValue, watch } = useFormContext<AddTransferTypeFormData>();
  const { isFetching, data } = useGetVehicleCategoriesQuery({ page_size: "999" });

  const categories = useMemo(() => {
    if (!data) return [];
    return data.categories;
  }, [data]);

  const transferTypeName = watch("name");
  const excludedCategories = watch("excludedCategories");

  const onCheckChanged = useCallback(
    (categoryId: string) => {
      if (excludedCategories.includes(categoryId)) {
        setValue(
          "excludedCategories",
          excludedCategories.filter((id) => id !== categoryId),
          {
            shouldDirty: true,
          }
        );
      } else
        setValue("excludedCategories", [...excludedCategories, categoryId], {
          shouldDirty: true,
        });
    },
    [excludedCategories, setValue]
  );

  const onCheckAllChanged = useCallback(
    (checked: boolean) => {
      setValue("excludedCategories", checked ? categories.map((category) => category.uuid) : [], {
        shouldDirty: true,
      });
    },
    [setValue, categories]
  );

  return (
    <div className="col-span-2 mt-5 flex flex-col">
      <header className="mb-3 ">
        <div className="flex w-full items-center">
          <Typography variant="action">Manage Vehicle Category Exclusions</Typography>
          <Typography className="flex-1 text-right text-primary">
            {excludedCategories.length > 0 ? `${excludedCategories.length} Vehicle Categories Excluded` : ""}
          </Typography>
        </div>
        {transferTypeName && (
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Select vehicle categories that are NOT applicable for “{transferTypeName}”
          </Typography>
        )}
      </header>
      {isFetching ? (
        <div className="flex h-[400px] w-full flex-col items-center justify-center gap-4 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray">
          <Spinner className="h-8 w-8 border-neutral-dark-gray" />
          <Typography className="text-neutral-dark-gray">Updating Vehicle Category List</Typography>
        </div>
      ) : (
        <ExcludedCategoriesTable
          items={categories}
          checkedIds={excludedCategories}
          onCheckChanged={onCheckChanged}
          onCheckAllChanged={onCheckAllChanged}
        />
      )}
    </div>
  );
};
